/**
 * Gerador pseudoaleatório determinístico da Batalha Infinita.
 *
 * Mesmo algoritmo LCG já usado em `waveGenerator.ts` e no store — replicado
 * aqui (em vez de importado) porque este módulo é a base de contratos, cartas
 * e perigos de terreno, e não pode depender do store nem carregar `three`.
 */
export function seededRandom(seed: number): () => number {
  let s = seed >>> 0;
  if (s === 0) s = 1;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

/** Hash FNV-1a de partes arbitrárias em um seed positivo estável. */
export function deterministicSeed(...parts: Array<string | number>): number {
  const key = parts.map(String).join("|");
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const normalized = hash & 0x7fffffff;
  return normalized === 0 ? 1 : normalized;
}

/** Semente nova para uma corrida. Única fonte de aleatoriedade real do modo. */
export function createRunSeed(): number {
  return deterministicSeed(Date.now(), Math.floor(Math.random() * 0xffffff));
}

/**
 * Semente da "corrida do dia": todo mundo que jogar no mesmo dia recebe
 * exatamente a mesma sequência de arenas, contratos e cartas oferecidas.
 */
export function dailySeed(date: Date = new Date()): number {
  return deterministicSeed(
    "daily",
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );
}

/** Escolhe `count` itens distintos sem repetição, respeitando pesos. */
export function weightedSample<T>(
  pool: readonly T[],
  weightOf: (item: T) => number,
  count: number,
  random: () => number,
): T[] {
  const remaining = pool.slice();
  const picked: T[] = [];

  while (picked.length < count && remaining.length > 0) {
    let total = 0;
    for (const item of remaining) total += Math.max(0, weightOf(item));

    if (total <= 0) {
      // Todos com peso zero: cai para escolha uniforme em vez de travar.
      picked.push(remaining.splice(Math.floor(random() * remaining.length), 1)[0]);
      continue;
    }

    let roll = random() * total;
    let index = remaining.length - 1;
    for (let i = 0; i < remaining.length; i++) {
      roll -= Math.max(0, weightOf(remaining[i]));
      if (roll <= 0) {
        index = i;
        break;
      }
    }
    picked.push(remaining.splice(index, 1)[0]);
  }

  return picked;
}
